import { getData } from "Agentic Module/api/getData";
import { postData } from "Agentic Module/api/postData";
import {
  EMAIL_TRIAGE_BASE_URL,
  appendEmailTriageSortParams,
} from "../emailTriaging/emailTriageApi";
import {
  MASTER_DATA_PAGE_SIZE,
  MASTER_FIELD_TO_LOOKUP_TYPE,
  apiFilterParamToInternalKey,
  internalFilterKeyToApiParam,
} from "./tntLinkageConstants";

export const UNSAVED_CHANGES_DISCARD_TITLE = "Discard unsaved changes?";

export const UNSAVED_CHANGES_DISCARD_MESSAGE =
  'You have unsaved linkage actions. If you continue, those changes will be lost. Use "Save Actions" to keep them, or choose to discard and continue.';

/** Map UI row action state → API `po_linkage_status`. */
export function mapUiStateToPoLinkageStatus(uiState) {
  if (uiState === "matched") return "approved";
  if (uiState === "user-validated") return "user_validated";
  if (uiState === "rejected") return "rejected";
  return String(uiState ?? "");
}

/** Build one `/api/v1/tnt/action` item from pending row action + overrides. */
export function buildTntActionItem(uiRowId, rowState, rowOverride = {}) {
  const override = rowOverride && typeof rowOverride === "object" ? rowOverride : {};
  return {
    ui_row_id: String(uiRowId),
    po_linkage_status: mapUiStateToPoLinkageStatus(rowState),
    manual_selected_site_code: String(override.siteCode ?? ""),
    manual_selected_material_code: String(override.materialCode ?? ""),
    manual_selected_vendor_code: String(override.vendorCode ?? ""),
    remarks: String(override.correctionRemark ?? ""),
  };
}

/**
 * POST `/api/v1/tnt/action` — persist confirmed linkage actions.
 */
export async function tntLinkageSaveActions({ items, reviewerId, reviewedTs }) {
  return postData(`${EMAIL_TRIAGE_BASE_URL}/api/v1/tnt/action`, {
    items,
    reviewer_id: reviewerId,
    reviewed_ts: reviewedTs,
  });
}

/** UI filter state keys → query param names for `/api/v1/tnt/get_filters`. */
function filterKeyToApiParamKey(internalKey) {
  return internalFilterKeyToApiParam(internalKey);
}

function toOptionStrings(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter((v) => v !== "");
}

/**
 * Map GET `/api/v1/tnt/get_filters` body to internal filter option lists.
 * All response keys are mapped dynamically — no hardcoded field list.
 */
export function mapTntFiltersResponse(raw) {
  if (!raw || typeof raw !== "object") return {};
  const result = {};
  Object.entries(raw).forEach(([apiKey, value]) => {
    const internalKey = apiFilterParamToInternalKey(apiKey);
    if (!internalKey) return;
    result[internalKey] = toOptionStrings(value);
  });
  return result;
}

export function appendTntFilterParams(params, filters) {
  if (!filters || typeof filters !== "object") return;
  Object.entries(filters).forEach(([key, value]) => {
    const apiKey = filterKeyToApiParamKey(key);
    if (value == null) return;
    if (Array.isArray(value)) {
      value
        .filter((v) => v != null && String(v).trim() !== "")
        .forEach((v) => params.append(apiKey, String(v)));
      return;
    }
    const v = String(value).trim();
    if (v !== "") params.set(apiKey, v);
  });
}

/**
 * GET `/api/v1/tnt/get_filters` — optional current selections as query params to narrow option lists.
 */
export async function tntLinkageGetFilters(filters) {
  const params = new URLSearchParams();
  appendTntFilterParams(params, filters);
  const url = `${EMAIL_TRIAGE_BASE_URL}/api/v1/tnt/get_filters`;
  const data = await postData(url, { filters });
  return mapTntFiltersResponse(data);
}

function pickNonEmptyString(...values) {
  for (const v of values) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s !== "") return s;
  }
  return "";
}

function resolveLinkedValue(
  hasSavedLinkageAction,
  manualValue,
  fallbackValue,
) {
  return hasSavedLinkageAction && manualValue
    ? manualValue
    : fallbackValue;
}

function getManualSelections(item) {
  return {
    siteCode: pickNonEmptyString(
      item.manual_selected_site_code,
      item.manualSelectedSiteCode,
    ),

    vendorCode: pickNonEmptyString(
      item.manual_selected_vendor_code,
      item.manualSelectedVendorCode,
    ),

    materialCode: pickNonEmptyString(
      item.manual_selected_material_code,
      item.manualSelectedMaterialCode,
    ),

    siteName: pickNonEmptyString(
      item.manual_selected_site_name,
      item.manualSelectedSiteName,
      item.site_name,
    ),

    vendorName: pickNonEmptyString(
      item.manual_selected_vendor_name,
      item.manualSelectedVendorName,
      item.vendor_name,
    ),

    materialName: pickNonEmptyString(
      item.manual_selected_material_name,
      item.manualSelectedMaterialName,
      item.material_name,
    ),
  };
}

/** Normalize one `/api/v1/tnt/get_tnt_data` row for the linkage table UI. */
export function normalizeTntDataRow(item) {
  if (!item || typeof item !== "object") return null;

  const manual = getManualSelections(item);

  const poLinkageStatus = item.poLinkageStatus ?? item.po_linkage_status ?? null;
  const hasSavedLinkageAction =
    poLinkageStatus != null && String(poLinkageStatus).trim() !== "";

  const defaultSiteCluster =
    item.siteCluster != null && String(item.siteCluster).trim() !== ""
      ? String(item.siteCluster)
      : "—";

  const siteCode = resolveLinkedValue(
    hasSavedLinkageAction,
    manual.siteCode,
    pickNonEmptyString(item.siteCode, item.site_code),
  );

  const siteCluster = resolveLinkedValue(
    hasSavedLinkageAction,
    manual.siteName,
    defaultSiteCluster,
  );

  const vendorCode = resolveLinkedValue(
    hasSavedLinkageAction,
    manual.vendorCode,
    pickNonEmptyString(item.vendorCode, item.vendor_code),
  );

  const agentVendorName = resolveLinkedValue(
    hasSavedLinkageAction,
    manual.vendorName,
    pickNonEmptyString(
      item.agentVendorName,
      item.agent_vendor_name,
    ),
  );

  const materialCode = resolveLinkedValue(
    hasSavedLinkageAction,
    manual.materialCode,
    pickNonEmptyString(
      item.materialCode,
      item.material_code,
    ),
  );

  const agentMaterialDesc = resolveLinkedValue(
    hasSavedLinkageAction,
    manual.materialName,
    pickNonEmptyString(
      item.agentMaterialDesc,
      item.agent_material_desc,
    ),
  );
  return {
    uiRowId: item.uiRowId ?? String(item.srNo ?? ""),
    srNo: item.srNo,
    tripId: item.tripId ?? "",
    destination: item.destination ?? "",
    tntVendorCode: item.tntVendorCode ?? "",
    tntVendorName: item.vendorName ?? "",
    tntMaterialCode: item.tntMaterialCode ?? "",
    tntMaterialDesc: item.materialDesc ?? "",
    lastLocation: pickNonEmptyString(item.lastLocation, item.last_location),
    distanceRemaining: pickNonEmptyString(item.distanceRemaining, item.distance_remaining),
    dispatchDate: item.dispatchDate ?? "",
    siteCode,
    siteCluster,
    vendorCode,
    agentVendorName,
    materialCode,
    agentMaterialDesc,
    manualSelectedSiteCode: manual.siteCode,
    manualSelectedVendorCode: manual.vendorCode,
    manualSelectedMaterialCode: manual.materialCode,
    manualSelectedSiteName: manual.siteName,
    manualSelectedVendorName: manual.vendorName,
    manualSelectedMaterialName: manual.materialName,
    confidence: item.confidenceScorePct ?? 0,
    poNumber: item.poNumber ?? "",
    poDispatchDate: item.dispatchDate ?? "",
    dispatchDateTime: item.dispatchDateTime ?? "",
    correlationId: item.correlationId ?? "",
    poItemNumber: item.poItemNumber ?? "",
    poLinkageStatus,
  };
}

export function normalizeTntDataResponse(raw) {
  const items = Array.isArray(raw?.items)
    ? raw.items.map(normalizeTntDataRow).filter(Boolean)
    : [];
  const page = Number(raw?.page) || 1;
  const pageSize = Number(raw?.pageSize ?? raw?.page_size) || 20;
  const total = Number(raw?.total) || 0;
  const totalPages = Math.max(
    1,
    Number(raw?.totalPages ?? raw?.total_pages) || Math.ceil(total / pageSize) || 1,
  );
  return { items, page, pageSize, total, totalPages };
}

/**
 * GET `/api/v1/tnt/get_tnt_data` — paginated linkage rows with optional filter query params.
 */
export async function tntLinkageGetTntData({
  page = 1,
  pageSize = 20,
  filters,
  sort,
} = {}) {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("page_size", String(pageSize));
  appendTntFilterParams(params, filters);
  appendEmailTriageSortParams(params, sort);
  const url = `${EMAIL_TRIAGE_BASE_URL}/api/v1/tnt/get_tnt_data`;
  const data = await postData(url, { page, pageSize, filters, sort });
  return normalizeTntDataResponse(data);
}

/** Correction dropdown field → `/get_master_date` response / query keys. */
export const MASTER_FIELD_TO_API_PARAM = {
  siteCode: "site_code",
  vendorCode: "vendor",
  materialCode: "material_code",
};

export { MASTER_FIELD_TO_LOOKUP_TYPE, MASTER_DATA_PAGE_SIZE };

function normalizeMasterDataOption(item, fieldType) {
  if (item == null) return null;
  if (typeof item === "string" || typeof item === "number") {
    const code = String(item).trim();
    return code ? { code, name: code } : null;
  }
  if (typeof item !== "object") return null;

  let code = "";
  let name = "";

  if (fieldType === "siteCode") {
    code = String(item.site_code ?? item.code ?? "").trim();
    name = String(item.site_name ?? item.name ?? item.site_cluster ?? code).trim();
  } else if (fieldType === "vendorCode") {
    code = String(item.vendor_code ?? item.code ?? "").trim();
    name = String(item.vendor_name ?? item.name ?? code).trim();
  } else if (fieldType === "materialCode") {
    code = String(item.material_code ?? item.material_id ?? item.code ?? "").trim();
    name = String(item.material_name ?? item.name ?? item.material_desc ?? code).trim();
  } else {
    code = String(
      item.code ??
      item.site_code ??
      item.vendor_code ??
      item.material_id ??
      item.material_code ??
      item.id ??
      "",
    ).trim();
    name = String(
      item.name ??
      item.site_name ??
      item.vendor_name ??
      item.material_name ??
      item.description ??
      item.material_desc ??
      code,
    ).trim();
  }

  if (!code) return null;
  return { code, name: name || code };
}

function getMasterDataResponseKeys(fieldType) {
  return [
    MASTER_FIELD_TO_API_PARAM[fieldType],
    fieldType,
    fieldType === "vendorCode" ? "vendor_code" : null,
    fieldType === "materialCode" ? "material_code" : null,
  ].filter(Boolean);
}

function extractCorrectionOptionList(raw, responseKeys) {
  if (Array.isArray(raw)) {
    return raw;
  }

  if (!raw || typeof raw !== "object") {
    return [];
  }

  const directList = responseKeys.find((key) =>
    Array.isArray(raw[key]),
  );

  if (directList) {
    return raw[directList];
  }

  if (Array.isArray(raw.items)) {
    return raw.items;
  }

  if (Array.isArray(raw.data)) {
    return raw.data;
  }

  if (!raw.data || typeof raw.data !== "object") {
    return [];
  }

  const nestedList = responseKeys.find((key) =>
    Array.isArray(raw.data[key]),
  );

  return nestedList ? raw.data[nestedList] : [];
}

function buildUniqueCorrectionOptions(list, fieldType) {
  const seen = new Set();

  return list
    .map((item) =>
      normalizeMasterDataOption(item, fieldType),
    )
    .filter((option) => {
      if (!option || seen.has(option.code)) {
        return false;
      }

      seen.add(option.code);
      return true;
    })
    .sort((a, b) => a.code.localeCompare(b.code));
}

/**
 * Map GET `/api/v1/get_master_date` body to `{ code, name }[]` for correction dropdowns.
 */
export function mapMasterDataToCorrectionOptions(
  raw,
  fieldType,
) {
  const responseKeys =
    getMasterDataResponseKeys(fieldType);

  const list = extractCorrectionOptionList(
    raw,
    responseKeys,
  );

  return buildUniqueCorrectionOptions(
    list,
    fieldType,
  );
}

function extractMasterDataItems(raw, fieldType) {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== "object") return [];

  const paramKey = MASTER_FIELD_TO_API_PARAM[fieldType];
  const responseKeys = [
    "items",
    "results",
    "data",
    paramKey,
    fieldType,
    fieldType === "vendorCode" ? "vendor_code" : null,
    fieldType === "materialCode" ? "material_code" : null,
  ].filter(Boolean);

  for (const k of responseKeys) {
    if (Array.isArray(raw[k])) return raw[k];
    if (raw[k] && typeof raw[k] === "object" && Array.isArray(raw[k].items)) {
      return raw[k].items;
    }
  }
  return [];
}

/**
 * Normalize paginated `/api/v1/get_master_date` for correction dropdowns.
 */
export function normalizeMasterDataResponse(raw, fieldType, { page = 1, pageSize = MASTER_DATA_PAGE_SIZE } = {}) {
  const list = extractMasterDataItems(raw, fieldType);
  const items = mapMasterDataToCorrectionOptions(list, fieldType);
  const resolvedPage = Number(raw?.page) || page;
  const resolvedPageSize = Number(raw?.pageSize ?? raw?.page_size) || pageSize;
  const total = Number(raw?.total ?? raw?.total_count ?? raw?.totalItems ?? raw?.count);
  const totalPages = Number(raw?.totalPages ?? raw?.total_pages);
  let hasMore;
  if (Number.isFinite(totalPages) && totalPages > 0) {
    hasMore = resolvedPage < totalPages;
  } else if (Number.isFinite(total) && total > 0) {
    hasMore = resolvedPage * resolvedPageSize < total;
  } else {
    hasMore = items.length >= resolvedPageSize;
  }
  return {
    items,
    page: resolvedPage,
    pageSize: resolvedPageSize,
    total: Number.isFinite(total) ? total : items.length,
    hasMore,
  };
}

/**
 * GET `/api/v1/get_master_date` — paginated master lists for correction dropdowns.
 * @param {{ search?: string, lookupType?: string, page?: number, pageSize?: number, site_code?: string, vendor_code?: string, material_code?: string }} [options]
 */
export async function tntLinkageGetMasterData({
  search,
  lookupType,
  page = 1,
  pageSize = MASTER_DATA_PAGE_SIZE,
  site_code,
  vendor_code,
  material_code,
} = {}) {
  const params = new URLSearchParams();
  const q = search != null ? String(search).trim() : "";
  if (q) params.set("search", q);
  const lookup = lookupType != null ? String(lookupType).trim() : "";
  if (lookup) params.set("lookup_type", lookup);
  params.set("page", String(page));
  params.set("page_size", String(pageSize));

  const siteCode = site_code != null ? String(site_code).trim() : "";
  const vendorCode = vendor_code != null ? String(vendor_code).trim() : "";
  const materialCode = material_code != null ? String(material_code).trim() : "";
  if (siteCode) params.set("site_code", siteCode);
  if (vendorCode) params.set("vendor_code", vendorCode);
  if (materialCode) params.set("material_code", materialCode);

  const url = `${EMAIL_TRIAGE_BASE_URL}/api/v1/get_master_date?${params.toString()}`;
  return getData(url);
}

/**
 * GET `/api/v1/tnt/get_trip_details` — live trip tracking data for the tracking modal.
 */
export async function tntLinkageGetTripDetails(tripId) {
  const id = tripId != null ? String(tripId).trim() : "";
  if (!id) throw new Error("Trip ID is required");
  const params = new URLSearchParams({ trip_id: id });
  const url = `${EMAIL_TRIAGE_BASE_URL}/api/v1/tnt/get_trip_details?${params.toString()}`;
  return getData(url);
}
